// src/components/PrivacyPolicy.js

import React from 'react';
import { Box, Typography, Link, Divider } from '@mui/material';

export default function PrivacyPolicy() {
  return (
    <Box
      sx={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '20px',
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
      }}
    >
      {/* Main Privacy Policy Header */}
      <Typography variant="h4" gutterBottom>
        Privacy Policy
      </Typography>
      <Typography variant="body2" gutterBottom sx={{ fontStyle: 'italic' }}>
        Last Updated: 21/06/2026
      </Typography>
      <Divider sx={{ width: '100%', my: 2 }} />

      {/* Introduction */}
      <Typography variant="body1" paragraph>
        Welcome to MicGro Manager (the “Service”), provided by <strong>Hy-Gro Hydroponics LTD</strong> (“we,” “us,” or
        “our”). This Privacy Policy explains how we collect, use, disclose, and protect your personal data when you use
        the Service. By accessing or using MicGro Manager, you agree to the practices described in this policy.
      </Typography>
      <Typography variant="body1" paragraph>
        If you have any questions, please contact us at:
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Hy-Gro Hydroponics LTD</strong>
        <br />
        <strong>Email:</strong>{' '}
        <Link href="mailto:support@hy-gro.co.uk" underline="hover">
          support@hy-gro.co.uk
        </Link>.
        <br />
        United Kingdom
      </Typography>

      {/* 1. Scope */}
      <Typography variant="h6" gutterBottom>
        1. Scope of This Policy
      </Typography>
      <Typography variant="body1" paragraph>
        This policy applies to personal data we collect through MicGro Manager. It covers data about our direct users
        (the growers/farmers) as well as any information they input about their own customers or business contacts.
      </Typography>

      {/* 2. Data We Collect */}
      <Typography variant="h6" gutterBottom>
        2. Data We Collect
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>a) Account Information:</strong> We collect your name, email address, and any other information you voluntarily provide (e.g., farm name). We use Firebase Authentication to manage user logins securely.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>b) Customer Data:</strong> Our Service allows you to store information about your own customers, such as names, postal addresses, email addresses, phone numbers, order history, and other information you choose to store about your customers. This data is under your control as the user; we merely provide the platform to store and manage it.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>c) Usage Data:</strong> We may collect technical data such as IP address, device information, and usage logs to maintain and improve the Service’s functionality.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>d) Analytics Data:</strong> We use Firebase Analytics to collect anonymised data about how users interact with the Service. This includes session duration, features used, and navigation patterns, which help us improve functionality. In the future, we may also use Sentry to help diagnose bugs and monitor system health, which may involve basic usage metadata.
      </Typography>

      {/* Cookies & Tracking */}
      <Typography variant="h6" gutterBottom>
        Cookies &amp; Tracking
      </Typography>
      <Typography variant="body1" paragraph>
        We use cookies and similar tracking technologies to provide and improve our Service. You will be presented with an in-app consent banner where you can choose to opt into:
      </Typography>
      <Typography component="ul" sx={{ pl: 2, mb: 2 }}>
        <li>
          <strong>Essential Cookies:</strong> Always active to support core functionality (e.g., authentication).
        </li>
        <li>
          <strong>Analytics Cookies:</strong> Load Google Analytics 4 to collect anonymized usage metrics.
        </li>
        <li>
          <strong>Session-Replay Cookies:</strong> Load Highlight for optional session replay, console logging, and network tracing.
        </li>
      </Typography>
      <Typography variant="body1" paragraph>
        You can manage or withdraw your consent at any time via the “Manage Cookies” tab at the bottom of the screen. If you revoke consent, we will stop loading the corresponding services and, upon request, delete any stored data collected under that category.
      </Typography>

      {/* 3. How We Use Your Data */}
      <Typography variant="h6" gutterBottom>
        3. How We Use Your Data
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>a) Service Provision:</strong> To create and manage your account, and to store and process the customer data you upload.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>b) Service Improvement:</strong> To analyze usage trends and improve our features, user interface, and overall performance.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>c) Communication and Account Information:</strong> We only use your signup email address to contact you regarding your account or Service updates. Any additional data you provide in the Account Page, including business name, contact name, phone number, business or farm address, VAT registration number, and invoicing email address, is stored solely to generate invoices, populate business documents, provide account functionality, and personalise your experience within the Service. This information is not used for marketing purposes.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>d) Legal and Compliance:</strong> To comply with applicable laws, regulations, and legal processes, and to enforce our Terms of Service.
      </Typography>

      {/* 4. Legal Basis for Processing */}
      <Typography variant="h6" gutterBottom>
        4. Legal Basis for Processing (GDPR)
      </Typography>
      <Typography variant="body1" paragraph>
        If you are located in the UK or the European Economic Area (EEA), we rely on one or more of the following legal
        bases for processing your personal data:
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Contract:</strong> Processing is necessary to provide the Service, including maintaining your account and storing the data you upload.
        <br />
        <strong>Legitimate Interests:</strong> We may process certain data to improve our Service, prevent fraud, and secure our platform.
        <br />
        <strong>Consent:</strong> Where required by law, we will obtain your consent before processing your personal data for specific purposes.
      </Typography>

      {/* 5. Your Role as a Data Controller */}
      <Typography variant="h6" gutterBottom>
        5. Your Role as a Data Controller
      </Typography>
      <Typography variant="body1" paragraph>
        When you use MicGro Manager to store data about your own customers or contacts, you act as the “Data Controller”
        for that information. You are responsible for ensuring the personal data you collect and store is processed
        lawfully and that you have the necessary permissions from your customers.
      </Typography>

      {/* 6. Data Sharing and Transfers */}
      <Typography variant="h6" gutterBottom>
        6. Data Sharing and Transfers
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Service Providers:</strong> We currently use the following third-party providers to operate and improve
        the Service:
      </Typography>
      <Typography component="ul" sx={{ pl: 2, mb: 2 }}>
        <li>
          <strong>Firebase (Google)</strong> for authentication, database storage, file storage, and analytics.
        </li>
        <li>
          <strong>Google Analytics 4</strong> for usage analytics where consent has been provided.
        </li>
        <li>
          <strong>Highlight</strong> for session replay, debugging, console logging, and network tracing where consent
          has been provided.
        </li>
        <li>
          <strong>Sentry</strong> for error monitoring and diagnostics, if enabled.
        </li>
      </Typography>
      <Typography variant="body1" paragraph>
        These providers process data on our behalf and are contractually required to protect personal data in accordance
        with applicable data protection laws.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>International Transfers:</strong> Firebase may store data on servers located in the European Union or
        the United States. We rely on appropriate safeguards (such as Standard Contractual Clauses) to ensure adequate
        protection of your personal data.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Legal Requirements:</strong> We may disclose personal data if required by law, court order, or
        government regulation, or if such disclosure is necessary in support of a legal or regulatory investigation.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Business Transfers:</strong> If we are involved in a merger, acquisition, or asset sale, personal data
        may be transferred to a third party as part of that transaction.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Third Parties:</strong> We do not sell or share your personal data with third parties for advertising or external use. Service providers like Firebase and Sentry only process data on our behalf to help operate and maintain the Service.
      </Typography>

      {/* 7. Data Retention */}
      <Typography variant="h6" gutterBottom>
        7. Data Retention
      </Typography>
      <Typography variant="body1" paragraph>
        We retain your personal data for as long as your account remains active or as otherwise necessary to provide
        the Service.
      </Typography>
      <Typography variant="body1" paragraph>
        Users may permanently delete their account directly through the Service. Doing so deletes the account and all
        associated data held within the Service. Account deletion cannot be undone.
      </Typography>
      <Typography variant="body1" paragraph>
        Users may also request deletion of their account and associated data by contacting us at{' '}
        <Link href="mailto:support@hy-gro.co.uk" underline="hover">
          support@hy-gro.co.uk
        </Link>
        .
      </Typography>

      {/* 8. Data Security */}
      <Typography variant="h6" gutterBottom>
        8. Data Security
      </Typography>
      <Typography variant="body1" paragraph>
        We take reasonable steps to protect personal data from unauthorized access, alteration, disclosure, or
        destruction. However, no data transmission or storage system can be guaranteed 100% secure.
      </Typography>

      {/* 9. Your Rights */}
      <Typography variant="h6" gutterBottom>
        9. Your Rights (UK & EEA Residents)
      </Typography>
      <Typography variant="body1" paragraph>
        If you are located in the UK or EEA, you have certain rights regarding your personal data, including the right to
        access, rectify, erase, restrict, or object to processing, the right to data portability, and the right to
        withdraw consent at any time where processing is based on consent. To exercise these rights, please contact us at
        <strong> support@hy-gro.co.uk</strong>.
      </Typography>

      {/* 10. Children’s Privacy */}
      <Typography variant="h6" gutterBottom>
        10. Children’s Privacy
      </Typography>
      <Typography variant="body1" paragraph>
        MicGro Manager is not intended for use by children under 16. We do not knowingly collect personal data from
        children. If you believe a child has provided us with personal data, please contact us so we can remove it.
      </Typography>

      {/* 11. Third-Party Links */}
      <Typography variant="h6" gutterBottom>
        11. Third-Party Links
      </Typography>
      <Typography variant="body1" paragraph>
        Our Service may contain links to third-party websites or services that we do not control. We are not responsible
        for the privacy practices of these third parties.
      </Typography>

      {/* 12. Changes to This Policy */}
      <Typography variant="h6" gutterBottom>
        12. Changes to This Policy
      </Typography>
      <Typography variant="body1" paragraph>
        We may update this Privacy Policy from time to time. If we make significant changes, we will notify you via
        email or within the Service. The “Last Updated” date at the top indicates when this policy was last revised.
      </Typography>

      {/* 13. Contact Us */}
      <Typography variant="h6" gutterBottom>
        13. Contact Us
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Hy-Gro Hydroponics LTD</strong>
        <br />
        <strong>Email:</strong>{' '}
        <Link href="mailto:support@hy-gro.co.uk" underline="hover">
          support@hy-gro.co.uk
        </Link>.
        <br />
        United Kingdom
      </Typography>

      {/* Disclaimer */}
      <Typography variant="body2" paragraph sx={{ mt: 2, fontStyle: 'italic' }}>
        Disclaimer: This policy is for informational purposes only and does not constitute legal advice. Please consult
        an attorney to ensure full compliance with applicable laws and regulations.
      </Typography>

      <Divider sx={{ width: '100%', my: 4 }} />

      {/* Beta Program Privacy Policy Addendum */}
      <Typography variant="h4" paragraph>
        Beta Program Privacy Policy Addendum
      </Typography>
      <Typography variant="body1" paragraph>
        This addendum supplements our main Privacy Policy and applies specifically to participants in the beta testing
        program for the MicGro Manager web application (“Service”). By participating in the beta program, you acknowledge
        that you have read and agree to this addendum.
      </Typography>

      <Typography variant="h6" gutterBottom>
        1. Scope
      </Typography>
      <Typography variant="body1" paragraph>
        This addendum covers all personal and usage data collected from beta testers between the start and end dates of
        the beta testing program.
      </Typography>
      <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
        Cookie Requirement
      </Typography>
      <Typography variant="body1" paragraph>
        To participate in this Beta Program, you must opt in to both our Analytics (Google Analytics 4) and Session-Replay 
        (Highlight) cookies using the in-app consent banner. If you decline or revoke that consent at any time, your Beta 
        access will be terminated and any unreleased features will no longer be available.
      </Typography>
      <Typography variant="h6" gutterBottom>
        2. Data Collected
      </Typography>
      <Typography component="ul" sx={{ pl: 2, mb: 2 }}>
        <li>Account information: email address, display name.</li>
        <li>Application usage data: pages visited, features used, session durations.</li>
        <li>Crash and error reports: stack traces, device/browser details.</li>
        <li>Survey responses and feedback: anonymized survey answers and comments.</li>
        <li>Support logs: communications via in-app bug reporter or support channels.</li>
        <li>Device information: operating system, browser version, IP address, timestamps.</li>
      </Typography>

      <Typography variant="h6" gutterBottom>
        3. Purpose of Processing
      </Typography>
      <Typography component="ul" sx={{ pl: 2, mb: 2 }}>
        <li>Identify and fix bugs or performance issues.</li>
        <li>Improve user experience and feature design.</li>
        <li>Analyze feature adoption and workflow effectiveness.</li>
        <li>Respond to support requests and follow up on feedback.</li>
      </Typography>

      <Typography variant="h6" gutterBottom>
        4. Data Sharing &amp; Confidentiality
      </Typography>
      <Typography variant="body1" paragraph>
        Collected data will be accessible only to authorized members of Hy-Gro Hydroponics LTD. We will not share
        personal data with third parties except as required by law.
      </Typography>

      <Typography variant="h6" gutterBottom>
        5. Data Retention
      </Typography>
      <Typography variant="body1" paragraph>
        Beta data will be retained for the duration of the beta plus 6 months, after which it will be securely deleted
        unless you request earlier removal.
      </Typography>

      <Typography variant="h6" gutterBottom>
        6. Your Rights
      </Typography>
      <Typography component="ul" sx={{ pl: 2, mb: 2 }}>
        <li><strong>Access:</strong> You may request a copy of the data we hold about you.</li>
        <li><strong>Deletion:</strong> You may request deletion of your beta data at any time.</li>
        <li><strong>Opt-Out:</strong> You may withdraw from the beta, stopping further data collection. If requested, we
          may delete previously collected beta data.
        </li>
      </Typography>

      <Typography variant="h6" gutterBottom>
        7. Contact Information
      </Typography>
      <Typography variant="body1" paragraph>
        For questions or data requests, contact us at{' '}
        <Link href="mailto:support@hy-gro.co.uk" underline="hover">
          support@hy-gro.co.uk
        </Link>.
      </Typography>
    </Box>
  );
}
